import {memo, useCallback, useEffect, useRef, useState} from 'react';
import {Box} from '@mui/material';
import {MoneyField} from '@/components/common';
import {fsCompactMoneyFieldSx} from '@/pages/components/fs-report/fsColumnSx';
import {PlFsReadonlyAmount} from '@/pages/components/fs-report/PlFsReadonlyAmount';

export type PlFsDeferredEditableAmountProps = {
    value: number;
    onChangeNumber: (num: number) => void;
    fontWeight?: number | string;
};

/**
 * MULTI FS first-paint optimization: show MoneyField-equivalent text until the user
 * focuses/clicks, then mount the real editable MoneyField. Numbers/layout unchanged.
 * SINGLE uses direct MoneyField (aligned with BalanceSheet editing UX).
 */
export const PlFsDeferredEditableAmount = memo(function PlFsDeferredEditableAmount({
    value,
    onChangeNumber,
    fontWeight,
}: PlFsDeferredEditableAmountProps) {
    const [editing, setEditing] = useState(false);
    const wrapRef = useRef<HTMLDivElement | null>(null);

    const startEditing = useCallback(() => setEditing(true), []);

    useEffect(() => {
        if (!editing) return;
        // Focus the input after MoneyField mounts
        const t = window.setTimeout(() => {
            const input = wrapRef.current?.querySelector('input');
            input?.focus();
            input?.select?.();
        }, 0);
        return () => window.clearTimeout(t);
    }, [editing]);

    if (!editing) {
        return (
            <Box
                role="button"
                tabIndex={0}
                onClick={startEditing}
                onFocus={startEditing}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        startEditing();
                    }
                }}
                sx={{
                    cursor: 'text',
                    minHeight: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    minWidth: 0,
                }}
            >
                <PlFsReadonlyAmount value={value} fontWeight={fontWeight}/>
            </Box>
        );
    }

    return (
        <Box ref={wrapRef} sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minWidth: 0,
        }}>
            <MoneyField
                value={value}
                showCents
                align="center"
                tiny
                compact
                editable
                onChangeNumber={onChangeNumber}
                sx={fsCompactMoneyFieldSx}
            />
        </Box>
    );
});
